import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Building2,
    ChevronDown,
    Globe2,
    LayoutDashboard,
    LogIn,
    LogOut,
    Menu,
    MessageSquare,
    Repeat2,
    ShieldCheck,
    Sparkles,
    User,
    UserPlus,
    X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from './NotificationBell';
import Select from '../ui/Select';
import {
    GOOGLE_TRANSLATE_LANGUAGES,
    applyGoogleTranslateLanguage,
    getStoredTranslateLanguage,
} from '../../utils/googleTranslate';
import './Navbar.css';

// Small faith symbol next to the logo for logged-in members (files live in /public).
const FAITH_SYMBOLS = {
    // Hindu members see two Om designs that swap every 2 seconds (CSS animation, runs forever)
    hindu: ['/om-a-sm.webp', '/om-b-sm.webp'],
    christian: '/cross-sm.webp',
    muslim: '/islamic-faith-symbol-sm.webp',
};

const Navbar =({ onLoginClick, onRegisterClick }) => {
    const { user, logout, linkedRoles, switchRole } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const [browseOpen, setBrowseOpen] = useState(false);
    const headerRef = useRef(null);

    // Translations (Tamil, Malayalam, …) are much longer than English. Instead of hiding the menu, shrink
    // the menu text a little at a time until it fits between the logo and the right-hand actions.
    useLayoutEffect(() => {
        const header = headerRef.current;
        if (!header) return undefined;
        let frame = 0;
        const fit = () => {
            const links = [...header.querySelectorAll('.navbar-menu .navbar-link')].filter(e => e.offsetParent);
            const actions = header.querySelector('.navbar-actions');
            if (!links.length || !actions) { header.style.removeProperty('--nav-scale'); return; }
            const visibleActions = [...actions.children].filter(e => e.offsetParent);
            if (!visibleActions.length) return;
            let scale = 1;
            header.style.setProperty('--nav-scale', '1');
            const overflow = () => Math.max(...links.map(e => e.getBoundingClientRect().right)) > Math.min(...visibleActions.map(e => e.getBoundingClientRect().left)) - 6;
            while (scale > 0.5 && overflow()) {
                scale = Math.round((scale - 0.03) * 100) / 100;
                header.style.setProperty('--nav-scale', String(scale));
            }
        };
        const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(fit); };
        schedule();
        window.addEventListener('resize', schedule);
        const observer = new MutationObserver(schedule); // Google Translate rewrites the text after load
        observer.observe(header, { childList: true, subtree: true, characterData: true });
        return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', schedule); observer.disconnect(); };
    }, []);

    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [lang, setLang] = useState(getStoredTranslateLanguage());

    const browseRef = useRef(null);
    const userMenuRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
            if (browseRef.current && !browseRef.current.contains(e.target)) {
                setBrowseOpen(false);
            }
        };

        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setUserMenuOpen(false);
                setBrowseOpen(false);
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const handleLanguageChange = code => {
        setLang(code);
        applyGoogleTranslateLanguage(code);
    };

    const closeMenus = () => {
        setMenuOpen(false);
        setBrowseOpen(false);
        setUserMenuOpen(false);
    };

    const go = path => {
        navigate(path);
        closeMenus();
    };

    const act = callback => {
        callback?.();
        closeMenus();
    };

    const targetRole = user?.role === 'member' ? 'service' : 'member';
    const canSwitchRole = Boolean(
        user
        && ['member', 'service'].includes(user.role)
        && linkedRoles.includes(targetRole)
    );

    const handleSwitchRole = async () => {
        try {
            const switchedUser = await switchRole(targetRole);
            toast.success(`Switched to ${targetRole === 'service' ? 'Service Provider' : 'Member'} account`);
            go(switchedUser.role === 'service' ? '/service-provider' : '/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not switch account');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Tamil comes from our own ta.json; keep Google Translate from re-translating (and garbling) it.
    const own = text => (lang === 'ta' ? <span className="notranslate" translate="no">{text}</span> : text);

    const navLinks = [
        { label: t('nav.home'), path: '/' },
        { label: t('nav.weddingServices'), path: '/services' },
        { label: t('nav.horoscope'), path: '/horoscope' },
        { label: t('nav.blog'), path: '/blog' },
    ];

    const accountLabel = (user?.profileFor && user?.profileFor !== 'Myself' ? user?.profileName : null) || user?.profileName || user?.name || user?.businessName || 'Account';
    const accountInitial = accountLabel.charAt(0).toUpperCase();
    const isActive = path => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
    const faithKey = String(user?.religion || '').trim().toLowerCase();
    const faithSymbols = [].concat(FAITH_SYMBOLS[faithKey] || []);

    const roleBadgeText = user?.role === 'admin'
        ? t('nav.roleAdmin')
        : user?.role === 'service'
            ? t('nav.roleService')
            : (user?.isPremium ? t('nav.rolePremium') : t('nav.roleMember'));

    const roleFullTitle = user?.role === 'admin'
        ? t('nav.roleAdministrator')
        : user?.role === 'service'
            ? t('nav.roleServicePartner')
            : (user?.isPremium ? t('nav.rolePremiumMember') : t('nav.roleActiveMember'));

    return (
        <header ref={headerRef} className={`site-navbar${user ? ' site-navbar--member' : ''}`} aria-label="Primary navigation">
            <div className="navbar-inner">
                {/* Brand / Logo */}
                <div className="navbar-brand">
                    <button type="button" className="navbar-logo" onClick={() => go('/')} aria-label="Gettimelam Home">
                        <img src="/logo-sm.webp" alt="Gettimelam" className="navbar-logo-img" />
                    </button>
                    {faithSymbols.length > 0 && (
                        <span
                            className={`navbar-faith-symbol navbar-faith-symbol--${faithKey}${faithSymbols.length > 1 ? ' navbar-faith-symbol--swap' : ''}`}
                            aria-hidden="true"
                        >
                            {faithSymbols.map((src, index) => (
                                <img
                                    key={src}
                                    src={src}
                                    alt=""
                                    className={`navbar-faith-symbol__img is-${index === 0 ? 'a' : 'b'}`}
                                />
                            ))}
                        </span>
                    )}
                </div>

                {/* Mobile Menu Toggle Button */}
                <button
                    type="button"
                    className="navbar-toggle"
                    onClick={() => setMenuOpen(open => !open)}
                    aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                    aria-expanded={menuOpen}
                >
                    {menuOpen ? <X size={22} /> : <Menu size={22} />}
                </button>

                {/* Main Navigation and Actions */}
                <div className={`navbar-menu${menuOpen ? ' navbar-menu-open' : ''}`}>
                    {/* Primary Navigation Links */}
                    <nav className="navbar-links" aria-label="Main menu">
                        <button
                            type="button"
                            className={`navbar-link${isActive(navLinks[0].path) ? ' is-active' : ''}`}
                            onClick={() => go(navLinks[0].path)}
                        >
                            {own(navLinks[0].label)}
                        </button>

                        {/* Browse Profiles Dropdown */}
                        <div
                            className={`navbar-dropdown${browseOpen ? ' is-open' : ''}`}
                            ref={browseRef}
                            onMouseEnter={() => setBrowseOpen(true)}
                            onMouseLeave={() => setBrowseOpen(false)}
                        >
                            <button
                                type="button"
                                className={`navbar-link navbar-browse-trigger${isActive('/browse') ? ' is-active' : ''}`}
                                onClick={() => setBrowseOpen(open => !open)}
                                aria-haspopup="menu"
                                aria-expanded={browseOpen}
                            >
                                <span>{own(t('nav.browseProfiles'))}</span>
                                <ChevronDown size={14} className="navbar-browse-chevron" />
                            </button>

                            {/* Always mounted (hidden by CSS): Google Translate reliably translates text present at load, but often misses nodes mounted later */}
                            {(
                                <div className={`navbar-drop-menu${browseOpen ? ' is-open' : ''}`} role="menu" aria-hidden={!browseOpen}>
                                    <button type="button" className="navbar-drop-item" onClick={() => go('/browse')}>
                                        Browse all profiles
                                    </button>
                                    <div className="navbar-drop-divider" />
                                    {['Hindu', 'Christian', 'Muslim'].map(religion => (
                                        <button
                                            type="button"
                                            key={religion}
                                            className="navbar-drop-item"
                                            onClick={() => go(`/browse?religion=${religion}`)}
                                        >
                                            {religion} Matrimony
                                        </button>
                                    ))}
                                    <div className="navbar-drop-divider" />
                                    <button type="button" className="navbar-drop-item" onClick={() => go('/browse?browseBy=location')}>
                                        By location
                                    </button>
                                    <button type="button" className="navbar-drop-item" onClick={() => go('/browse?browseBy=profession')}>
                                        By profession
                                    </button>
                                    <button type="button" className="navbar-drop-item" onClick={() => go('/browse?maritalStatus=Divorced&browseBy=maritalStatus')}>
                                        Divorced
                                    </button>
                                </div>
                            )}
                        </div>

                        {navLinks.slice(1).map(link => {
                            const isServices = link.path === '/services';
                            return (
                                <button
                                    type="button"
                                    key={link.path}
                                    className={`navbar-link${isActive(link.path) ? ' is-active' : ''}`}
                                    onClick={() => go(link.path)}
                                >
                                    <span>{own(link.label)}</span>
                                </button>
                            );
                        })}
                    </nav>

                    {/* Right-Hand Actions Group */}
                    <div className="navbar-actions">
                        {/* Messages Icon — only for members */}
                        {user?.role === 'member' && (
                            <button
                                type="button"
                                className={`navbar-icon-btn${isActive('/messages') ? ' is-active' : ''}`}
                                onClick={() => go('/messages')}
                                title="Messages"
                                aria-label="Messages"
                            >
                                <MessageSquare size={19} />
                            </button>
                        )}

                        {/* Notification Bell */}
                        {user && <NotificationBell />}

                        {/* Language Selector */}
                        <div className="navbar-language notranslate" translate="no" title="Translate website">
                            <Globe2 size={15} aria-hidden="true" />
                            <Select
                                className="navbar-lang-picker"
                                value={lang}
                                onChange={handleLanguageChange}
                                options={GOOGLE_TRANSLATE_LANGUAGES.map(language => ({ value: language.code, label: language.native }))}
                                allowEmpty={false}
                                menuMinWidth={190}
                                selectedLabel={lang.toUpperCase()}
                                ariaLabel="Website language"
                            />
                        </div>

                        {user ? (
                            <div className="navbar-user-controls">
                                {/* User Profile Interactive Dropdown */}
                                <div className="navbar-user-dropdown-wrap" ref={userMenuRef}>
                                    <button
                                        type="button"
                                        className={`navbar-user-trigger${userMenuOpen ? ' is-open' : ''}`}
                                        onClick={() => setUserMenuOpen(prev => !prev)}
                                        aria-haspopup="menu"
                                        aria-expanded={userMenuOpen}
                                        title={accountLabel}
                                    >
                                        <div className="navbar-user-avatar">
                                            {accountInitial}
                                        </div>
                                        <div className="navbar-user-meta">
                                            <span className="navbar-user-name">{accountLabel}</span>
                                            <span className="navbar-user-badge">{roleBadgeText}</span>
                                        </div>
                                        <ChevronDown size={14} className={`navbar-user-chevron${userMenuOpen ? ' is-rotated' : ''}`} />
                                    </button>

                                    {userMenuOpen && (
                                        <div className="navbar-user-menu-card" role="menu">
                                            <div className="navbar-user-card__header">
                                                <div className="navbar-user-card__avatar">
                                                    {accountInitial}
                                                </div>
                                                <div className="navbar-user-card__info">
                                                    <span className="navbar-user-card__name">{accountLabel}</span>
                                                    <span className="navbar-user-card__sub">{user.mobile || user.email || 'Tamil Matrimony'}</span>
                                                    <span className={`navbar-user-card__tag${user.isPremium ? ' is-premium' : ''}`}>
                                                        {roleFullTitle}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="navbar-user-card__divider" />

                                            <div className="navbar-user-card__links">
                                                {user.role === 'member' && (
                                                    <button
                                                        type="button"
                                                        className={`navbar-user-card__item${location.pathname === '/dashboard' ? ' is-active' : ''}`}
                                                        onClick={() => go('/dashboard')}
                                                    >
                                                        <LayoutDashboard size={16} />
                                                        <span>{t('nav.dashboard')}</span>
                                                    </button>
                                                )}
                                                {user.role === 'service' && (
                                                    <button
                                                        type="button"
                                                        className={`navbar-user-card__item${location.pathname === '/service-provider' ? ' is-active' : ''}`}
                                                        onClick={() => go('/service-provider')}
                                                    >
                                                        <Building2 size={16} />
                                                        <span>{t('nav.myBusiness')}</span>
                                                    </button>
                                                )}
                                                {user.role === 'admin' && (
                                                    <button
                                                        type="button"
                                                        className={`navbar-user-card__item${location.pathname === '/admin' ? ' is-active' : ''}`}
                                                        onClick={() => go('/admin')}
                                                    >
                                                        <ShieldCheck size={16} />
                                                        <span>{t('nav.adminPanel')}</span>
                                                    </button>
                                                )}

                                                {user.role === 'admin' && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={`navbar-user-card__item${location.pathname === '/messages' ? ' is-active' : ''}`}
                                                            onClick={() => go('/messages')}
                                                        >
                                                            <MessageSquare size={16} />
                                                            <span>{t('nav.messages')}</span>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className={`navbar-user-card__item${location.pathname === '/plans' ? ' is-active' : ''}`}
                                                            onClick={() => go('/plans')}
                                                        >
                                                            <Sparkles size={16} />
                                                            <span>{t('nav.plans')}</span>
                                                        </button>

                                                        {canSwitchRole && (
                                                            <button
                                                                type="button"
                                                                className="navbar-user-card__item navbar-user-card__item--switch"
                                                                onClick={handleSwitchRole}
                                                            >
                                                                <Repeat2 size={16} />
                                                                <span>Switch to {targetRole === 'service' ? 'Service Partner' : 'Member'}</span>
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            <div className="navbar-user-card__divider" />

                                            <button
                                                type="button"
                                                className="navbar-user-card__item navbar-user-card__item--logout"
                                                onClick={() => act(handleLogout)}
                                            >
                                                <LogOut size={16} />
                                                <span>{t('nav.logout')}</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="navbar-guest-actions">
                                <button type="button" className="navbar-guest-login" onClick={() => act(onLoginClick)}>
                                    <LogIn size={15} />
                                    <span>{t('nav.login') || 'Login'}</span>
                                </button>
                                <button type="button" className="navbar-guest-register" onClick={() => act(onRegisterClick)}>
                                    <UserPlus size={15} />
                                    <span>{t('nav.register') || 'Free Register'}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
